/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { 
  Calculator, 
  ArrowRight, 
  ChevronRight, 
  Sparkles, 
  TrendingUp, 
  Percent, 
  Briefcase, 
  Coins,
  ShieldCheck,
  Building2,
  Clock,
  Calendar,
  Home,
  Users,
  Baby,
  Heart,
  Car,
  GraduationCap,
  Scale
} from 'lucide-react';

export interface RelatedCalculatorsProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  defaultCategory?: 'all' | 'steuern' | 'arbeit-gehalt' | 'soziales' | 'zeit-datum';
  title?: string;
  subtitle?: string;
}

export function RelatedCalculators({ 
  currentPath, 
  onNavigate,
  defaultCategory = 'all',
  title = "Passende Rechner derselben Kategorie",
  subtitle = "Ergänzen Sie Ihre Gehalts- und Steuerplanung mit weiteren amtlich geprüften Online-Rechnern für Deutschland."
}: RelatedCalculatorsProps) {
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'steuern' | 'arbeit-gehalt' | 'soziales' | 'zeit-datum'>(defaultCategory);

  // All available portal calculators categorized
  const tools = [
    {
      id: 'bruttorechner',
      name: 'Brutto Rechner (Wunsch-Netto zu Brutto)',
      path: '/bruttorechner',
      category: 'steuern',
      categoryLabel: 'Gehalt & Steuern',
      description: 'Geben Sie Ihr gewünschtes Netto ein und berechnen Sie das dafür erforderliche Bruttogehalt für 2027 & 2026.',
      icon: Calculator,
      color: 'text-sky-600 bg-sky-50 border-sky-100',
      badge: 'Neu 2027',
    },
    {
      id: 'brutto-netto',
      name: 'Brutto-Netto-Rechner',
      path: '/brutto-netto',
      category: 'steuern',
      categoryLabel: 'Gehalt & Steuern',
      description: 'Ermitteln Sie Ihr Netto-Gehalt, die genauen Steuern und die paritätischen Sozialabgaben aus Ihrem Bruttolohn.',
      icon: Coins,
      color: 'text-emerald-600 bg-emerald-50 border-emerald-100',
      badge: 'Sehr beliebt',
    },
    {
      id: 'einkommensteuer',
      name: 'Einkommensteuer-Rechner',
      path: '/einkommensteuer',
      category: 'steuern',
      categoryLabel: 'Steuern',
      description: 'Berechnen Sie Ihre jährliche Einkommensteuer samt Durchschnitts- und Grenzsteuersatz für Singles oder Verheiratete.',
      icon: TrendingUp,
      color: 'text-blue-700 bg-blue-50 border-blue-100',
      badge: 'Empfohlen',
    },
    {
      id: 'lohnsteuerrechner',
      name: 'Lohnsteuerrechner',
      path: '/lohnsteuerrechner',
      category: 'steuern',
      categoryLabel: 'Steuern',
      description: 'Errechnen Sie Lohnsteuer, Kirchensteuer und Solidaritätszuschlag exakt für Ihren monatlichen Abrechnungszeitraum.',
      icon: Scale,
      color: 'text-purple-600 bg-purple-50 border-purple-100',
      badge: 'Monatslohn',
    },
    {
      id: 'steuer-berechnen',
      name: 'Steuerrechner (Steuer berechnen)',
      path: '/steuer-berechnen',
      category: 'steuern',
      categoryLabel: 'Steuern',
      description: 'Umfassende Gesamtsteuer-Kalkulation inkl. ESt, Lohnsteuer, Soli und Kirchensteuer für das laufende Steuerjahr.',
      icon: Coins,
      color: 'text-indigo-600 bg-indigo-50 border-indigo-100',
      badge: 'All-in-One',
    },
    {
      id: 'abgeltungsteuerrechner',
      name: 'Abgeltungsteuerrechner',
      path: '/abgeltungsteuerrechner',
      category: 'steuern',
      categoryLabel: 'Kapitalertrag',
      description: 'Kapitalertragsteuer auf Zinsen, Dividenden und ETFs mit Sparer-Pauschbetrag & Freistellungsauftrag berechnen.',
      icon: Percent,
      color: 'text-amber-600 bg-amber-50 border-amber-100',
      badge: 'Kapital 2027',
    },
    {
      id: 'aussergewoehnliche-belastungen',
      name: 'Außergewöhnliche Belastungen',
      path: '/aussergewoehnliche-belastungen',
      category: 'steuern',
      categoryLabel: 'Steuerabzug',
      description: 'Zumutbare Belastung nach § 33 EStG ermitteln sowie Krankheits-, Pflege- und Pflegeheimkosten steuerlich geltend machen.',
      icon: ShieldCheck,
      color: 'text-teal-600 bg-teal-50 border-teal-100',
      badge: '§ 33 EStG',
    },
    {
      id: 'erbschaftsteuer-rechner',
      name: 'Erbschaftsteuer-Rechner 2027 & 2026',
      path: '/erbschaftsteuer-rechner',
      category: 'steuern',
      categoryLabel: 'Erbschaft & Vermögen',
      description: 'Erbschaftsteuer, Freibeträge nach Steuerklasse I–III, Familienheim-Befreiung und Erbfallkostenpauschale berechnen.',
      icon: Scale,
      color: 'text-blue-900 bg-blue-50 border-blue-100',
      badge: 'Neu 2027',
    },
    {
      id: 'nettopreis-rechner',
      name: 'Umsatzsteuer- / Nettopreis-Rechner',
      path: '/nettopreis-rechner',
      category: 'steuern',
      categoryLabel: 'Umsatzsteuer',
      description: 'Preise schnell und präzise von Brutto zu Netto oder Netto zu Brutto umrechnen mit 19 % und 7 % MwSt.',
      icon: Percent,
      color: 'text-rose-600 bg-rose-50 border-rose-100',
      badge: 'Schnell-Tool',
    },
    {
      id: 'abfindungsrechner',
      name: 'Abfindungsrechner (Fünftelregelung)',
      path: '/abfindungsrechner',
      category: 'arbeit-gehalt',
      secondaryCategory: 'steuern' as const,
      categoryLabel: 'Arbeitsrecht & Steuern',
      description: 'Steuerersparnis durch die steuerliche Fünftelregelung nach § 34 EStG bei Aufhebungsvertrag und Entlassung berechnen.',
      icon: Briefcase,
      color: 'text-blue-600 bg-blue-50 border-blue-100',
      badge: 'Fünftelregelung',
    },
    {
      id: 'arbeitszeitrechner',
      name: 'Arbeitszeit- & Überstundenrechner',
      path: '/arbeitszeitrechner',
      category: 'zeit-datum',
      categoryLabel: 'Zeit & Datum',
      description: 'Tägliche und wöchentliche Arbeitsstunden, Pausenabzüge und Überstundensaldi nach Arbeitszeitgesetz (ArbZG) kalkulieren.',
      icon: Clock,
      color: 'text-slate-700 bg-slate-100 border-slate-200',
      badge: 'ArbZG-konform',
    },
    {
      id: 'arbeitslosengeld-rechner',
      name: 'Arbeitslosengeld-Rechner (ALG 1)',
      path: '/arbeitslosengeld-rechner',
      category: 'arbeit-gehalt',
      categoryLabel: 'Sozialleistung',
      description: 'Anspruch auf ALG 1 (60 % bzw. 67 % mit Kind) anhand des bisherigen Bruttogehalts präzise berechnen.',
      icon: ShieldCheck,
      color: 'text-amber-700 bg-amber-50 border-amber-100',
      badge: 'SGB III',
    },
    {
      id: 'wohngeldrechner',
      name: 'Wohngeldrechner (Wohngeld Plus)',
      path: '/wohngeldrechner',
      category: 'soziales',
      categoryLabel: 'Wohnen & Soziales',
      description: 'Staatlichen Mietzuschuss nach der Wohngeld-Plus-Reform anhand von Haushaltseinkommen und Mietenstufe ermitteln.',
      icon: Home,
      color: 'text-teal-700 bg-teal-50 border-teal-100',
      badge: 'Wohngeld Plus',
    },
    {
      id: 'buergergeld-rechner',
      name: 'Bürgergeld-Rechner',
      path: '/buergergeld-rechner',
      category: 'soziales',
      categoryLabel: 'Grundsicherung',
      description: 'Regelsatz, Mehrbedarfe, Wohnkosten (KdU) und Freibeträge bei Erwerbseinkommen nach SGB II berechnen.',
      icon: Users,
      color: 'text-indigo-700 bg-indigo-50 border-indigo-100',
      badge: 'SGB II',
    },
    {
      id: 'elterngeld-rechner',
      name: 'Elterngeldrechner',
      path: '/elterngeld-rechner',
      category: 'soziales',
      categoryLabel: 'Familie',
      description: 'Basiselterngeld und ElterngeldPlus nach BEEG mit Geschwisterbonus und Einkommensgrenzen berechnen.',
      icon: Baby,
      color: 'text-pink-600 bg-pink-50 border-pink-100',
      badge: 'BEEG',
    },
    {
      id: 'elternzeitrechner',
      name: 'Elternzeitrechner',
      path: '/elternzeitrechner',
      category: 'soziales',
      categoryLabel: 'Familie',
      description: 'Monate und Fristen für Mutter und Vater mit Antragsfristen nach BEEG vorausschauend planen.',
      icon: Baby,
      color: 'text-rose-600 bg-rose-50 border-rose-100',
      badge: 'Fristenplaner',
    },
    {
      id: 'bafoeg-rechner',
      name: 'BAföG-Rechner',
      path: '/bafoeg-rechner',
      category: 'soziales',
      categoryLabel: 'Bildung',
      description: 'Monatliche BAföG-Förderung für Studierende und Schüler mit Freibeträgen für elterliches Einkommen kalkulieren.',
      icon: GraduationCap,
      color: 'text-cyan-700 bg-cyan-50 border-cyan-100',
      badge: 'Studium & Schule',
    },
    {
      id: 'laengenumrechner',
      name: 'Längenumrechner (Einheiten)',
      path: '/laengenumrechner',
      category: 'arbeit-gehalt',
      categoryLabel: 'Einheiten',
      description: 'Präzise Längen- und Distanzmaße zwischen Millimeter, Meter, Zoll, Fuß, Meilen und nautischen Einheiten umrechnen.',
      icon: Scale,
      color: 'text-violet-600 bg-violet-50 border-violet-100',
      badge: 'Umrechner',
    },
    {
      id: 'altersrechner',
      name: 'Altersrechner (Exaktes Alter)',
      path: '/altersrechner',
      category: 'zeit-datum',
      categoryLabel: 'Zeit & Datum',
      description: 'Exaktes Alter in Jahren, Monaten, Tagen, Stunden & Sekunden nach BGB berechnen inkl. Wochentag, Schaltjahr & Sternzeichen.',
      icon: Clock,
      color: 'text-indigo-600 bg-indigo-50 border-indigo-100',
      badge: 'Neu 2027',
    },
    {
      id: 'arbeitstage-rechner',
      name: 'Arbeitstage-Rechner 2027 & 2026',
      path: '/arbeitstage-rechner',
      category: 'zeit-datum',
      categoryLabel: 'Zeit & Datum',
      description: 'Arbeitstage für alle 16 Bundesländer exakt berechnen. Feiertage, Wochenenden, Urlaub & Homeoffice für die Pendlerpauschale.',
      icon: Calendar,
      color: 'text-blue-600 bg-blue-50 border-blue-100',
      badge: 'Beliebt',
    },
    {
      id: 'zeitrechner',
      name: 'Zeitrechner 2026/2027',
      path: '/zeitrechner',
      category: 'zeit-datum',
      categoryLabel: 'Zeit & Datum',
      description: 'Zeiten addieren, Zeitspanne mit Nachtschicht berechnen, Industrieminuten & Dezimalstunden für die Lohnabrechnung ermitteln.',
      icon: Clock,
      color: 'text-amber-600 bg-amber-50 border-amber-100',
      badge: 'Neu 2026/2027',
    },
    {
      id: 'datumsrechner',
      name: 'Datumsrechner 2026/2027',
      path: '/datumsrechner',
      category: 'zeit-datum',
      categoryLabel: 'Zeit & Datum',
      description: 'Datum berechnen, Tage/Monate addieren, 2 Berechnungswege vergleichen & gesetzliche Fristen nach BGB § 193 ermitteln.',
      icon: Calendar,
      color: 'text-blue-600 bg-blue-50 border-blue-100',
      badge: 'Neu 2026/2027',
    },
    {
      id: 'et-rechner',
      name: 'ET Rechner (Geburtstermin)',
      path: '/et-rechner',
      category: 'soziales',
      categoryLabel: 'Familie & Gesundheit',
      description: 'Voraussichtlichen Entbindungstermin nach erweiterter Naegele-Regel, Empfängnis oder Ultraschall berechnen. Inkl. Mutterschutz gem. § 3 MuSchG.',
      icon: Baby,
      color: 'text-rose-600 bg-rose-50 border-rose-100',
      badge: 'Neu 2026/2027',
    },
    {
      id: 'ssw-rechner',
      name: 'SSW Rechner & Trimester',
      path: '/ssw-rechner',
      category: 'soziales',
      categoryLabel: 'Familie & Gesundheit',
      description: 'Aktuelle Schwangerschaftswoche (SSW + Tage), Trimester, Fötusgröße mit Frucht-Vergleich & offizielle Mutterpass-Vorsorgetermine berechnen.',
      icon: Baby,
      color: 'text-purple-600 bg-purple-50 border-purple-100',
      badge: 'Neu 2026/2027',
    },
    {
      id: 'et-rechner-felgen',
      name: 'ET Rechner Felgen',
      path: '/et-rechner-felgen',
      category: 'zeit-datum',
      categoryLabel: 'Kfz & Technik',
      description: 'Einpresstiefe, Felgenbreite & Distanzscheiben: Verschiebung der Felgenaußenkante und Innenkante mit TÜV 2%-Prüfung nach VdTÜV 751 berechnen.',
      icon: Car,
      color: 'text-amber-600 bg-amber-50 border-amber-100',
      badge: 'Neu 2026/2027',
    },
    {
      id: 'eisprungrechner',
      name: 'Eisprungrechner & Fruchtbarkeit',
      path: '/eisprungrechner',
      category: 'soziales',
      categoryLabel: 'Familie & Gesundheit',
      description: 'Tag des Eisprungs, fruchtbares 6-Tage-Fenster, Einnistung und Entbindungstermin mit individueller Lutealphäsen-Dauer berechnen.',
      icon: Heart,
      color: 'text-pink-600 bg-pink-50 border-pink-100',
      badge: 'Neu 2026/2027',
    },
  ];

  // Helper to normalize paths for comparison
  const normalize = (p: string) => {
    let clean = (p || '').toLowerCase().trim();
    if (clean.startsWith('#')) clean = clean.slice(1);
    if (clean.endsWith('.html')) clean = clean.slice(0, -5);
    if (clean.endsWith('/')) clean = clean.slice(0, -1);
    return clean;
  };

  const currentNormalized = normalize(currentPath);

  // Filter out the current path so we never recommend the page the user is currently on
  const availableTools = tools.filter((tool) => {
    const toolNorm = normalize(tool.path);
    if (toolNorm === currentNormalized) return false;
    if (`/steuerrechner${toolNorm}` === currentNormalized) return false;
    if (`/arbeit-soziales${toolNorm}` === currentNormalized) return false;
    if (currentNormalized.replace(/-/g, '') === toolNorm.replace(/-/g, '')) return false;
    if (currentNormalized.includes(tool.id)) return false;
    return true;
  });

  // Category filtering
  const filteredTools = availableTools.filter((tool) => {
    if (selectedCategory === 'all') return true;
    return tool.category === selectedCategory || (tool as any).secondaryCategory === selectedCategory;
  });

  const categories = [
    { id: 'all', label: 'Alle Rechner', count: availableTools.length },
    { id: 'zeit-datum', label: 'Zeit & Datum', count: availableTools.filter(t => t.category === 'zeit-datum' || (t as any).secondaryCategory === 'zeit-datum').length },
    { id: 'steuern', label: 'Steuern & Lohn', count: availableTools.filter(t => t.category === 'steuern' || (t as any).secondaryCategory === 'steuern').length },
    { id: 'arbeit-gehalt', label: 'Arbeit & Gehalt', count: availableTools.filter(t => t.category === 'arbeit-gehalt' || (t as any).secondaryCategory === 'arbeit-gehalt').length },
    { id: 'soziales', label: 'Soziales & Familie', count: availableTools.filter(t => t.category === 'soziales' || (t as any).secondaryCategory === 'soziales').length },
  ];

  return (
    <div className="w-full min-w-0 max-w-full space-y-3 sm:space-y-5" id="related-calculators-section">
      {/* Header section with responsive typography and badge */}
      <div className="space-y-2.5 sm:space-y-3 pb-3 sm:pb-4 border-b border-slate-200/80 w-full min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 w-full min-w-0">
          <div className="min-w-0 flex-1">
            <h3 className="text-base sm:text-lg lg:text-xl font-black text-slate-900 tracking-tight flex items-center gap-2">
              <span className="p-1 sm:p-1.5 rounded-lg bg-blue-100 text-blue-700 shrink-0">
                <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </span>
              <span className="break-words">{title}</span>
            </h3>
            <p className="text-xs sm:text-sm text-slate-500 mt-1 leading-relaxed max-w-3xl break-words">
              {subtitle}
            </p>
          </div>
          <span className="self-start sm:self-center text-[10px] sm:text-[11px] font-bold text-blue-700 bg-blue-50 border border-blue-200/70 px-2.5 py-1 rounded-full shrink-0">
            {availableTools.length} Rechner verfügbar
          </span>
        </div>

        {/* Responsive Category Filter Pills:
            - On mobile: Horizontal smooth swipe container without breaking layout or causing page horizontal scroll
            - On tablet/desktop: Clean flex-wrap pill buttons
        */}
        <div className="w-full min-w-0 overflow-hidden">
          <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-1 sm:pb-0 pt-0.5 -mx-1 px-1 sm:mx-0 sm:px-0 sm:flex-wrap scrollbar-none">
            {categories.map((cat) => {
              const isSelected = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setSelectedCategory(cat.id as any)}
                  className={`text-[11px] sm:text-xs font-bold px-2.5 sm:px-3.5 py-1.5 sm:py-2 rounded-xl border transition-all duration-150 cursor-pointer whitespace-nowrap shrink-0 flex items-center gap-1.5 active:scale-[0.98] ${
                    isSelected
                      ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                      : 'bg-slate-50 text-slate-700 border-slate-200/90 hover:bg-slate-100 hover:border-slate-300'
                  }`}
                >
                  <span>{cat.label}</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold ${
                    isSelected ? 'bg-blue-700 text-white' : 'bg-slate-200/90 text-slate-700'
                  }`}>
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Grid of Related Calculators:
          - 1 column on mobile (< 640px)
          - 2 columns on tablet (640px - 1024px)
          - 3 columns on desktop (1024px+)
      */}
      {filteredTools.length === 0 ? (
        <div className="text-center py-6 sm:py-8 px-4 bg-slate-50/80 rounded-2xl border border-slate-200/80 text-slate-500 text-xs sm:text-sm">
          Keine weiteren Rechner in dieser Kategorie vorhanden.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-3.5 lg:gap-4 w-full min-w-0">
          {filteredTools.map((tool) => {
            const IconComponent = tool.icon;
            return (
              <button
                key={tool.path}
                type="button"
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  try {
                    window.history.pushState(null, '', tool.path);
                  } catch (e) {}
                  onNavigate(tool.path);
                }}
                className="w-full text-left bg-slate-50/70 hover:bg-white active:bg-slate-100 border border-slate-200/90 hover:border-blue-400 hover:shadow-md p-3 sm:p-4 rounded-xl sm:rounded-2xl transition-all duration-200 cursor-pointer group relative overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 min-w-0 flex flex-col justify-between"
              >
                <div className="flex items-start gap-2.5 sm:gap-3.5 w-full min-w-0">
                  {/* Icon Container */}
                  <div className={`p-2 sm:p-2.5 rounded-xl border shrink-0 mt-0.5 ${tool.color}`}>
                    <IconComponent className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>

                  {/* Text Information */}
                  <div className="space-y-1 sm:space-y-1.5 flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1.5 min-w-0 w-full">
                      <span className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-wider text-slate-400 truncate">
                        {tool.categoryLabel}
                      </span>
                      {tool.badge && (
                        <span className="text-[10px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200/70 shrink-0 whitespace-nowrap">
                          {tool.badge}
                        </span>
                      )}
                    </div>

                    <h4 className="font-extrabold text-slate-900 text-xs sm:text-sm group-hover:text-blue-600 transition-colors leading-snug break-words line-clamp-2">
                      {tool.name}
                    </h4>

                    <p className="text-[11px] sm:text-xs text-slate-500 leading-relaxed line-clamp-2 break-words">
                      {tool.description}
                    </p>
                  </div>
                </div>

                {/* Footer Action Link */}
                <div className="flex items-center justify-between pt-2.5 sm:pt-3 border-t border-slate-200/60 mt-2 sm:mt-2.5 w-full min-w-0">
                  <span className="text-[11px] sm:text-xs font-bold text-blue-600 group-hover:text-blue-700 transition-colors flex items-center gap-1">
                    Rechner öffnen
                    <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform shrink-0" />
                  </span>
                  <span className="text-[10px] font-semibold text-slate-400 font-mono hidden sm:inline">
                    Kostenlos
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
